import { DrawerDialogComponent } from '@theme/drawer-dialog';
import { getLongestCompositeTime, onAnimationEnd, parseCssTimeValue } from '@theme/utilities';

const RECENT_ACCORDION_STATE_STORAGE_KEY = 'vandox:header-drawer:recent-accordion-state';
const DRAWER_OPEN_CLASSES = ['drawer-open', 'menu-open'];

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The root dialog element.
 *
 * @extends {DrawerDialogComponent<Refs>}
 */
class HeaderDrawer extends DrawerDialogComponent {
  requiredRefs = ['dialog'];
  #defaultAccordionOpenTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#onKeyDown, true);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown, true);
    this.#clearDefaultAccordionOpenTimer();
  }

  /**
   * Close the closest open submenu before letting the root dialog handle Escape.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    const details = this.#getDetailsElement(event);
    if (!details) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#closeDetails(details);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.dialog.open;
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement | null}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return null;

    return event.target.closest('details');
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  afterDrawerOpen() {
    this.#queueDefaultAccordionOpen(this.refs.dialog);
  }

  afterDrawerClose() {
    this.#resetOpenSubmenus();
    this.refs.trigger?.focus({ preventScroll: true });
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    if (!details) {
      super.open();
      return;
    }

    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      details.classList.add(...DRAWER_OPEN_CLASSES);
      this.#queueDefaultAccordionOpen(details);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    const details = this.#getDetailsElement(event);
    if (!details) return;

    this.#closeDetails(details);
  }

  /**
   * Close the main menu drawer
   * @param {Event} [event]
   */
  close(event) {
    const details = this.#getDetailsElement(event);
    if (details) {
      this.#closeDetails(details);
      return;
    }

    this.#closeRoot();
  }

  closeRoot() {
    return this.#closeRoot();
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #closeDetails(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    this.#clearDefaultAccordionOpenTimer();
    summary.setAttribute('aria-expanded', 'false');
    details.classList.add('drawer-closing');
    details.classList.remove(...DRAWER_OPEN_CLASSES);

    const drawer = details.querySelector('.menu-drawer__submenu') || details;
    onAnimationEnd(drawer, () => {
      details.classList.remove('drawer-closing');
      reset(details);
      this.#resetNestedDetails(details);
      summary.focus({ preventScroll: true });
    }, { subtree: false });
  }

  async #closeRoot() {
    this.#clearDefaultAccordionOpenTimer();
    this.#storeRecentAccordionState();
    await super.close();
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.drawer-stagger-item');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  #queueDefaultAccordionOpen(root) {
    const accordions = root.querySelectorAll('accordion-custom[data-open-on-drawer]');
    if (!accordions.length) return;

    this.#clearDefaultAccordionOpenTimer();

    if (root === this.refs.dialog && this.#restoreRecentAccordionState()) {
      return;
    }

    accordions.forEach((accordion) => {
      const details = accordion.querySelector('details');
      if (!(details instanceof HTMLDetailsElement)) return;

      details.open = false;
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });

    this.#defaultAccordionOpenTimer = window.setTimeout(() => {
      this.#defaultAccordionOpenTimer = null;

      if (!this.refs.dialog.open || !root.hasAttribute('open')) return;

      accordions.forEach((accordion) => {
        const details = accordion.querySelector('details');
        if (!(details instanceof HTMLDetailsElement)) return;

        details.open = true;
        details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      });
    }, this.#getDefaultAccordionOpenDelay(root));
  }

  #clearDefaultAccordionOpenTimer() {
    if (this.#defaultAccordionOpenTimer === null) return;

    window.clearTimeout(this.#defaultAccordionOpenTimer);
    this.#defaultAccordionOpenTimer = null;
  }

  #getDefaultAccordionOpenDelay(root) {
    if (this.dataset.defaultAccordionOpenAuto === 'true') {
      return this.#getAutoAccordionOpenDelay(root);
    }

    return this.#getManualAccordionOpenDelay();
  }

  #getManualAccordionOpenDelay() {
    return parseCssTimeValue(getComputedStyle(this).getPropertyValue('--drawer-accordion-auto-open-delay'));
  }

  #getAutoAccordionOpenDelay(root) {
    let maxMotionEnd = this.#getElementMotionEndTime(root.querySelector('.menu-drawer, .menu-drawer__submenu') || root);
    const renderedParents = root.querySelectorAll(
      '.menu-drawer__menu-item--mainlist.drawer-stagger-item, .menu-drawer__utility-links.drawer-stagger-item'
    );

    renderedParents.forEach((element) => {
      maxMotionEnd = Math.max(maxMotionEnd, this.#getElementMotionEndTime(element));
    });

    return Math.ceil(maxMotionEnd + this.#getAutoAccordionOpenBuffer());
  }

  #getAutoAccordionOpenBuffer() {
    return parseCssTimeValue(getComputedStyle(this).getPropertyValue('--drawer-motion-auto-open-buffer'));
  }

  #getElementMotionEndTime(element) {
    if (!(element instanceof Element)) return 0;

    const style = getComputedStyle(element);

    return Math.max(
      getLongestCompositeTime(style.animationDelay, style.animationDuration),
      getLongestCompositeTime(style.transitionDelay, style.transitionDuration)
    );
  }

  #restoreRecentAccordionState() {
    const state = this.#getRecentAccordionState();
    if (!state) return false;

    this.#applyAccordionState(state.openDetailIds ?? []);
    return true;
  }

  #storeRecentAccordionState() {
    const preserveDuration = this.#getAccordionStatePreserveDuration();
    if (preserveDuration <= 0) {
      this.#clearRecentAccordionState();
      return;
    }

    const openDetailIds = this.#getAutoOpenAccordions()
      .map((accordion) => accordion.querySelector('details'))
      .filter((details) => details instanceof HTMLDetailsElement && details.open && details.id)
      .map((details) => details.id);

    this.#setRecentAccordionState({
      expiresAt: Date.now() + preserveDuration,
      openDetailIds,
    });
  }

  #applyAccordionState(openDetailIds) {
    const openIdSet = new Set(openDetailIds);

    this.#getAutoOpenAccordions().forEach((accordion) => {
      const details = accordion.querySelector('details');
      const summary = details?.querySelector('summary');

      if (!(details instanceof HTMLDetailsElement)) return;

      const shouldBeOpen = details.id ? openIdSet.has(details.id) : false;
      details.open = shouldBeOpen;
      summary?.setAttribute('aria-expanded', shouldBeOpen ? 'true' : 'false');
    });
  }

  #getAutoOpenAccordions() {
    return [...this.querySelectorAll('accordion-custom[data-open-on-drawer]')].filter(
      (accordion) => accordion instanceof HTMLElement
    );
  }

  #getAccordionStatePreserveDuration() {
    return parseCssTimeValue(getComputedStyle(this).getPropertyValue('--drawer-accordion-open-state-preserve-duration'));
  }

  #getRecentAccordionState() {
    if (this.#getAccordionStatePreserveDuration() <= 0) {
      this.#clearRecentAccordionState();
      return null;
    }

    try {
      const rawValue = window.sessionStorage.getItem(RECENT_ACCORDION_STATE_STORAGE_KEY);
      if (!rawValue) return null;

      const state = JSON.parse(rawValue);
      if (
        typeof state?.expiresAt !== 'number' ||
        !Array.isArray(state?.openDetailIds) ||
        state.expiresAt <= Date.now()
      ) {
        this.#clearRecentAccordionState();
        return null;
      }

      return state;
    } catch {
      return null;
    }
  }

  #setRecentAccordionState(state) {
    try {
      window.sessionStorage.setItem(RECENT_ACCORDION_STATE_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage write failures and fall back to stateless behavior.
    }
  }

  #clearRecentAccordionState() {
    try {
      window.sessionStorage.removeItem(RECENT_ACCORDION_STATE_STORAGE_KEY);
    } catch {
      // Ignore storage clear failures and fall back to stateless behavior.
    }
  }

  #resetNestedDetails(root) {
    root.querySelectorAll('details[open]').forEach((details) => {
      if (details instanceof HTMLDetailsElement) {
        reset(details);
      }
    });
  }

  #resetOpenSubmenus() {
    this.#resetNestedDetails(this);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove(...DRAWER_OPEN_CLASSES, 'drawer-closing');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
